import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContextBackend';

function formatTimeRemaining(expiresAt: Date): string {
  const now = new Date();
  const diff = expiresAt.getTime() - now.getTime();

  if (diff <= 0) {
    return 'Expired';
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

export default function ImpersonationBanner() {
  const { isImpersonating, impersonationData, user, account, endImpersonation } = useAuth();

  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [isEnding, setIsEnding] = useState(false);

  // Update countdown timer every second
  useEffect(() => {
    if (!isImpersonating || !impersonationData?.expiresAt) {
      return;
    }

    const updateTimer = () => {
      const remaining = formatTimeRemaining(impersonationData.expiresAt);
      setTimeRemaining(remaining);

      // Auto-end session if expired
      if (remaining === 'Expired') {
        endImpersonation();
      }
    };

    // Initial update
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [isImpersonating, impersonationData, endImpersonation]);

  const handleEndSession = async () => {
    if (isEnding) return;
    setIsEnding(true);
    await endImpersonation();
  };

  if (!isImpersonating || !user || !account) {
    return null;
  }

  const isExpiringSoon =
    impersonationData?.expiresAt &&
    impersonationData.expiresAt.getTime() - Date.now() < 5 * 60 * 1000;

  return (
    <div
      className={`h-9 px-4 flex items-center justify-between text-sm ${
        isExpiringSoon
          ? 'bg-gradient-to-r from-red-600 to-red-500'
          : 'bg-gradient-to-r from-amber-600 to-amber-500'
      } text-white`}
      style={{ zIndex: 9999 }}
    >
      <div className="flex items-center gap-3">
        {/* Impersonation indicator */}
        <div className="flex items-center gap-1.5">
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
          <span className="font-medium">
            Viewing as{' '}
            <strong>
              {user.first_name} {user.last_name}
            </strong>
          </span>
          <span className="text-white/70">({account.name})</span>
        </div>

        {/* Divider */}
        <div className="hidden sm:block h-3.5 w-px bg-white/30" />

        {/* Reason - hidden on small screens */}
        {impersonationData?.reason && (
          <span
            className="hidden sm:block text-white/80 truncate max-w-[200px]"
            title={impersonationData.reason}
          >
            {impersonationData.reason}
          </span>
        )}

        {/* Divider */}
        <div className="hidden sm:block h-3.5 w-px bg-white/30" />

        {/* Timer */}
        <div className="flex items-center gap-1.5">
          <svg
            className="h-3.5 w-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className={`font-medium tabular-nums ${isExpiringSoon ? 'animate-pulse' : ''}`}>
            {timeRemaining}
          </span>
        </div>
      </div>

      {/* End Session Button */}
      <button
        onClick={handleEndSession}
        disabled={isEnding}
        className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          isExpiringSoon
            ? 'bg-white/20 hover:bg-white/30 text-white'
            : 'bg-white/20 hover:bg-white/30 text-white'
        }`}
      >
        <svg
          className="h-3.5 w-3.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
        {isEnding ? 'Ending...' : 'End Session'}
      </button>
    </div>
  );
}
